import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  getDeliveryStatus,
  getDeliveryTeamDetails,
  getDeliveryUserContent,
  getDeliveryWeeks,
  getDeliveryYears,
  type DeliveryTeamSummary,
  type DeliveryUser,
  type DeliveryUserContent,
} from '../../api/adminClient';

function getCurrentWeek(): { week: number; year: number } {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  const hours = now.getUTCHours();
  let daysSinceWeekStart = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  if (dayOfWeek === 1 && hours < 8) {
    daysSinceWeekStart = 7;
  }
  const weekStart = new Date(now);
  weekStart.setUTCDate(now.getUTCDate() - daysSinceWeekStart);
  weekStart.setUTCHours(8, 0, 0, 0);
  const jan1 = new Date(Date.UTC(weekStart.getUTCFullYear(), 0, 1));
  const days = Math.floor((weekStart.getTime() - jan1.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + jan1.getUTCDay() + 1) / 7);
  return { week: weekNumber, year: weekStart.getUTCFullYear() };
}

function getWeekStartDate(week: number, year: number): Date {
  const jan4 = new Date(year, 0, 4);
  const jan4DayOfWeek = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - (jan4DayOfWeek - 1));
  const targetMonday = new Date(week1Monday);
  targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
  return targetMonday;
}

interface WeekInfo {
  week: number;
  start: Date;
  end: Date;
  label: string;
}

function getWeeksForMonth(year: number, month: number): WeekInfo[] {
  const weeks: WeekInfo[] = [];
  for (let w = 1; w <= 53; w++) {
    const start = getWeekStartDate(w, year);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    // A week belongs to a month if its Monday falls in that month
    if (start.getMonth() === month && start.getFullYear() === year) {
      const label = `W${w}: ${start.getMonth() + 1}/${start.getDate()}–${end.getMonth() + 1}/${end.getDate()}`;
      weeks.push({ week: w, start, end, label });
    }
  }
  return weeks;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function AdminRollups() {
  const current = useMemo(() => getCurrentWeek(), []);
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(current.year);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedWeek, setSelectedWeek] = useState(current.week);
  const [expandedCell, setExpandedCell] = useState<{ teamId: string | null; status: string } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Years from DB
  const { data: years } = useQuery({
    queryKey: ['delivery-years'],
    queryFn: getDeliveryYears,
  });

  // Weeks with data
  const { data: weeksData } = useQuery({
    queryKey: ['delivery-weeks', selectedYear],
    queryFn: () => getDeliveryWeeks(selectedYear),
  });

  const weeksSet = useMemo(() => new Set(weeksData?.weeks || []), [weeksData]);
  const firstYear = weeksData?.first_year ?? current.year;
  const firstWeek = weeksData?.first_week ?? 1;

  // Delivery status summary
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['delivery-status', selectedYear, selectedWeek],
    queryFn: () => getDeliveryStatus(selectedYear, selectedWeek),
  });

  // Team detail (lazy)
  const { data: teamUsers, isLoading: teamUsersLoading } = useQuery({
    queryKey: ['delivery-team', selectedYear, selectedWeek, expandedCell?.teamId, expandedCell?.status],
    queryFn: () => getDeliveryTeamDetails(selectedYear, selectedWeek, expandedCell!.teamId, expandedCell!.status),
    enabled: !!expandedCell,
  });

  // User content (lazy)
  const { data: userContent, isLoading: userContentLoading } = useQuery({
    queryKey: ['delivery-user', selectedYear, selectedWeek, selectedUserId],
    queryFn: () => getDeliveryUserContent(selectedYear, selectedWeek, selectedUserId!),
    enabled: !!selectedUserId,
  });

  // Weeks for the selected month
  const monthWeeks = useMemo(() => getWeeksForMonth(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

  // Auto-select current month when year changes
  useEffect(() => {
    if (selectedYear === current.year) {
      setSelectedMonth(currentMonth);
    } else {
      setSelectedMonth(0);
    }
    setExpandedCell(null);
    setSelectedUserId(null);
  }, [selectedYear]);

  // Auto-select best week when month changes
  useEffect(() => {
    const weeks = getWeeksForMonth(selectedYear, selectedMonth);
    if (weeks.length === 0) return;

    // If current week is in this month, select it
    const currentInMonth = weeks.find((w) => w.week === current.week && selectedYear === current.year);
    if (currentInMonth) {
      setSelectedWeek(currentInMonth.week);
    } else {
      // Pick the latest week with data, or the latest week
      const withData = weeks.filter((w) => weeksSet.has(w.week));
      setSelectedWeek(withData.length > 0 ? withData[withData.length - 1].week : weeks[weeks.length - 1].week);
    }
    setExpandedCell(null);
    setSelectedUserId(null);
  }, [selectedMonth, selectedYear]);

  const handleCellClick = (teamId: string | null, status: string, count: number) => {
    if (count === 0) return;
    if (expandedCell?.teamId === teamId && expandedCell?.status === status) {
      setExpandedCell(null);
      setSelectedUserId(null);
    } else {
      setExpandedCell({ teamId, status });
      setSelectedUserId(null);
    }
  };

  const handleUserClick = (userId: string) => {
    setSelectedUserId(selectedUserId === userId ? null : userId);
  };

  const isMonthDisabled = (month: number) => {
    const now = new Date();
    // Future months in current year
    if (selectedYear === now.getFullYear() && month > now.getMonth()) return true;
    // Future years entirely
    if (selectedYear > now.getFullYear()) return true;
    // Months before first data in first year
    if (selectedYear === firstYear) {
      const firstWeekStart = getWeekStartDate(firstWeek, firstYear);
      if (month < firstWeekStart.getMonth()) return true;
    }
    // Years before first data
    if (selectedYear < firstYear) return true;
    return false;
  };

  const isWeekFuture = (weekInfo: WeekInfo) => {
    const now = new Date();
    return weekInfo.start > now;
  };

  const isWeekBeforeFirstData = (weekInfo: WeekInfo) => {
    if (selectedYear < firstYear) return true;
    if (selectedYear === firstYear && weekInfo.week < firstWeek) return true;
    return false;
  };

  const summary = statusData?.summary || { delivered: 0, failed: 0, pending: 0 };
  const teams: DeliveryTeamSummary[] = statusData?.teams || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Delivery Monitor</h1>
        <p className="text-gray-500 mt-1">Track scheduled report delivery by team</p>
      </div>

      {/* Week Picker */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        {/* Year selector + Month tabs */}
        <div className="flex items-center gap-4 mb-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            {(years || [current.year]).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="flex gap-1 flex-wrap">
            {MONTH_NAMES.map((name, i) => {
              const disabled = isMonthDisabled(i);
              const isActive = i === selectedMonth;
              return (
                <button
                  key={i}
                  onClick={() => !disabled && setSelectedMonth(i)}
                  disabled={disabled}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : disabled
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Week buttons for selected month */}
        <div className="flex flex-wrap gap-2">
          {monthWeeks
            .filter((w) => !isWeekBeforeFirstData(w))
            .map((w) => {
              const isCurrent = w.week === current.week && selectedYear === current.year;
              const hasData = weeksSet.has(w.week);
              const isSelected = w.week === selectedWeek;
              const future = isWeekFuture(w);
              return (
                <button
                  key={w.week}
                  onClick={() => {
                    if (!future) {
                      setSelectedWeek(w.week);
                      setExpandedCell(null);
                      setSelectedUserId(null);
                    }
                  }}
                  disabled={future}
                  className={clsx(
                    'relative px-3 py-2 text-xs rounded-lg font-medium transition-colors',
                    future
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      : isSelected
                        ? 'bg-primary-600 text-white'
                        : isCurrent
                          ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {w.label}
                  {hasData && !isSelected && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary-500" />
                  )}
                </button>
              );
            })}
          {monthWeeks.filter((w) => !isWeekBeforeFirstData(w)).length === 0 && (
            <span className="text-sm text-gray-400">No weeks in this month</span>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-3xl font-bold text-green-600">{summary.delivered}</p>
          <p className="text-sm text-gray-500">Delivered</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-3xl font-bold text-red-600">{summary.failed}</p>
          <p className="text-sm text-gray-500">Failed</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-3xl font-bold text-yellow-600">{summary.pending}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </div>
      </div>

      {/* Team Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            W{selectedWeek}-{selectedYear} by Team
          </h2>
        </div>

        {statusLoading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : teams.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No report-receiving users found</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-sm text-gray-500">
                <th className="px-4 py-3 font-medium">Team</th>
                <th className="px-4 py-3 font-medium text-center">Delivered</th>
                <th className="px-4 py-3 font-medium text-center">Failed</th>
                <th className="px-4 py-3 font-medium text-center">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {teams.map((team) => {
                const teamKey = team.team_id;
                const isExpanded = expandedCell?.teamId === teamKey;
                return (
                  <TeamRow
                    key={teamKey ?? '__no_team__'}
                    team={team}
                    isExpanded={isExpanded}
                    expandedStatus={isExpanded ? expandedCell!.status : null}
                    onCellClick={handleCellClick}
                    teamUsers={isExpanded ? teamUsers : undefined}
                    teamUsersLoading={isExpanded && teamUsersLoading}
                    selectedUserId={selectedUserId}
                    onUserClick={handleUserClick}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* User Content (lazy loaded) */}
      {selectedUserId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Content</h3>
          {userContentLoading ? (
            <div className="text-center text-gray-500">Loading report...</div>
          ) : !userContent || userContent.length === 0 ? (
            <div className="text-center text-gray-500">No report content found</div>
          ) : (
            <div className="space-y-4">
              {userContent.map((report: DeliveryUserContent) => (
                <ReportPreview key={report.id} report={report} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportPreview({ report }: { report: DeliveryUserContent }) {
  const [expanded, setExpanded] = useState(false);
  const content = report.formatted_text || JSON.stringify(report.content, null, 2);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-900 capitalize">{report.report_type} Report</span>
          <span className="text-sm text-gray-500">{report.user_name}</span>
          <StatusBadge status={report.status} />
          {report.error_message && (
            <span className="text-xs text-red-600 truncate max-w-[200px]" title={report.error_message}>
              {report.error_message}
            </span>
          )}
        </div>
        <span className="text-gray-400 text-sm">{expanded ? 'Collapse' : 'Preview'}</span>
      </button>
      {expanded && (
        <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono p-4 overflow-x-auto max-h-96 overflow-y-auto">
          {content}
        </pre>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles = {
    delivered: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    generated: 'bg-yellow-100 text-yellow-800',
  };
  const label = status === 'generated' ? 'Pending' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800')}>
      {label}
    </span>
  );
}

function TeamRow({
  team,
  isExpanded,
  expandedStatus,
  onCellClick,
  teamUsers,
  teamUsersLoading,
  selectedUserId,
  onUserClick,
}: {
  team: DeliveryTeamSummary;
  isExpanded: boolean;
  expandedStatus: string | null;
  onCellClick: (teamId: string | null, status: string, count: number) => void;
  teamUsers?: DeliveryUser[];
  teamUsersLoading: boolean;
  selectedUserId: string | null;
  onUserClick: (userId: string) => void;
}) {
  const CountCell = ({ count, status, color }: { count: number; status: string; color: string }) => {
    const isActive = isExpanded && expandedStatus === status;
    return (
      <td className="px-4 py-3 text-center">
        {count > 0 ? (
          <button
            onClick={() => onCellClick(team.team_id, status, count)}
            className={clsx(
              'inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-sm font-semibold transition-colors',
              isActive
                ? `${color} ring-2 ring-offset-1 ring-primary-400`
                : `${color} hover:opacity-80 cursor-pointer`
            )}
          >
            {count}
          </button>
        ) : (
          <span className="text-gray-300 text-sm">0</span>
        )}
      </td>
    );
  };

  return (
    <>
      <tr className={clsx(isExpanded && 'bg-gray-50')}>
        <td className="px-4 py-3 font-medium text-gray-900">
          {team.team_name}
          <span className="ml-2 text-xs text-gray-400">{team.total_users} users</span>
        </td>
        <CountCell count={team.delivered} status="delivered" color="bg-green-100 text-green-800" />
        <CountCell count={team.failed} status="failed" color="bg-red-100 text-red-800" />
        <CountCell count={team.pending} status="generated" color="bg-yellow-100 text-yellow-800" />
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={4} className="px-4 py-2 bg-gray-50">
            {teamUsersLoading ? (
              <div className="text-sm text-gray-500 py-2">Loading...</div>
            ) : !teamUsers || teamUsers.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">No users found</div>
            ) : (
              <div className="space-y-1 py-1">
                {teamUsers.map((user: DeliveryUser) => (
                  <button
                    key={user.id}
                    onClick={() => onUserClick(user.user_id)}
                    className={clsx(
                      'w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left',
                      selectedUserId === user.user_id
                        ? 'bg-primary-50 border border-primary-200'
                        : 'hover:bg-white'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-900">{user.user_name}</span>
                      <span className="text-gray-400 capitalize">{user.report_type} Report</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {user.source === 'manual_send' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Manual</span>
                      )}
                      <StatusBadge status={user.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
